import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getEvent, getGuests, importGuests, deleteGuest, regenerateQr } from '../services/api';
import { ArrowLeft, Upload, Trash2, Search, QrCode, RefreshCw, Filter, Download } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_LABELS = {
  all: 'Tous', pending: 'En attente', confirmed: 'Confirmés',
  declined: 'Déclinés', no_response: 'Sans réponse'
};

function StatusBadge({ status }) {
  const map = {
    confirmed:   { cls: 'badge-green',  label: 'Confirmé' },
    declined:    { cls: 'badge-red',    label: 'Décliné' },
    pending:     { cls: 'badge-amber',  label: 'En attente' },
    no_response: { cls: 'badge-gray',   label: 'Sans réponse' },
  };
  const { cls, label } = map[status] || { cls: 'badge-gray', label: status };
  return <span className={`badge ${cls}`}>{label}</span>;
}

export default function GuestsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [guests, setGuests] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const [evRes, gRes] = await Promise.all([getEvent(id), getGuests(id)]);
      setEvent(evRes.data);
      setGuests(gRes.data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const handleImport = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const { data } = await importGuests(id, file);
      toast.success(`${data.imported} invités importés${data.skipped > 0 ? `, ${data.skipped} ignorés` : ''}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur import');
    } finally {
      setImporting(false);
      fileRef.current.value = '';
    }
  };

  const handleDelete = async guest => {
    if (!window.confirm(`Supprimer ${guest.first_name} ${guest.last_name} ?`)) return;
    await deleteGuest(guest.id);
    toast.success('Invité supprimé');
    load();
  };

  const handleRegenQr = async guest => {
    try {
      await regenerateQr(guest.id);
      toast.success('QR code régénéré');
      load();
    } catch { toast.error('Erreur'); }
  };

  const filtered = guests.filter(g => {
    const matchFilter = filter === 'all' || g.rsvp_status === filter;
    const matchSearch = !search || `${g.first_name} ${g.last_name} ${g.phone}`.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const exportCSV = () => {
    const rows = [['Prénom', 'Nom', 'Téléphone', 'Statut RSVP', 'QR envoyé', 'Arrivé'].join(',')];
    filtered.forEach(g => {
      rows.push([g.first_name, g.last_name, g.phone, g.rsvp_status,
        g.qr_sent_at ? 'Oui' : 'Non', g.checked_in ? 'Oui' : 'Non'].join(','));
    });
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `invites_${event?.name || 'export'}.csv`; a.click();
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/events/${id}`)}>
          <ArrowLeft size={14} /> {event?.name || 'Retour'}
        </button>
      </div>

      <div className="page-header">
        <div>
          <h1>Invités</h1>
          <p className="text-muted">{guests.length} invité{guests.length !== 1 ? 's' : ''} · {guests.filter(g => g.rsvp_status === 'confirmed').length} confirmés</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-ghost btn-sm" onClick={exportCSV} disabled={filtered.length === 0}>
            <Download size={14} /> Export CSV
          </button>
          <button className="btn btn-primary" onClick={() => fileRef.current.click()} disabled={importing}>
            {importing ? <span className="spinner" /> : <Upload size={15} />}
            Import CSV
          </button>
          <input ref={fileRef} type="file" accept=".csv" onChange={handleImport} style={{ display: 'none' }} />
        </div>
      </div>

      {/* Format attendu */}
      <div style={{ background: 'rgba(79,124,255,0.06)', border: '1px solid rgba(79,124,255,0.2)', borderRadius: 'var(--radius)', padding: '10px 16px', marginBottom: 20, fontSize: 12.5, color: 'var(--text2)' }}>
        📋 Format CSV attendu : <code style={{ color: 'var(--accent)' }}>first_name, phone</code> (obligatoires) + <code style={{ color: 'var(--accent)' }}>last_name, table, note</code>... (optionnels)
      </div>

      {/* Filtres + recherche */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={14} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }} />
          <input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 32 }} />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <button key={k} className={`btn btn-sm ${filter === k ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFilter(k)}>
              {v} {k !== 'all' && <span style={{ opacity: 0.7 }}>({guests.filter(g => g.rsvp_status === k).length})</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <Upload />
            <h3>Aucun invité</h3>
            <p>Importez un fichier CSV pour ajouter vos invités.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Invité</th>
                  <th>Téléphone</th>
                  <th>RSVP</th>
                  <th>QR Code</th>
                  <th>Arrivée</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(g => (
                  <tr key={g.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{g.first_name} {g.last_name}</div>
                      {g.extra_data?.table && <div className="text-sm text-muted">{g.extra_data.table}</div>}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{g.phone}</td>
                    <td><StatusBadge status={g.rsvp_status} /></td>
                    <td>
                      {g.qr_sent_at
                        ? <span className="badge badge-green">✓ Envoyé</span>
                        : <span className="badge badge-gray">Non envoyé</span>}
                    </td>
                    <td>
                      {g.checked_in
                        ? <span className="badge badge-green">✓ {g.checked_in_at ? new Date(g.checked_in_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                        : <span className="text-muted text-sm">—</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {g.rsvp_status === 'confirmed' && (
                          <button className="btn btn-ghost btn-sm" title="Régénérer QR" onClick={() => handleRegenQr(g)}>
                            <RefreshCw size={12} />
                          </button>
                        )}
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(g)}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
